import { useState, useEffect } from 'react'
import { Globe, TrendingUp, ArrowRight, ShieldAlert } from 'lucide-react'
import { publicCompanies } from '../data/seed'
import { formatCurrency } from '../lib/format'
import AccessRequestModal from '../components/AccessRequestModal'
import InvestorVault from './InvestorVault'
import { SkeletonCardGrid } from '../components/ui/Skeleton'
import { useToast } from '../lib/ToastContext'
import { companiesApi, accessRequestsApi } from '../lib/api'

function readinessColor(score) {
  if (score >= 70) return 'text-emerald-700 bg-emerald-50 border-emerald-300'
  if (score >= 41) return 'text-amber-700 bg-amber-50 border-amber-300'
  return 'text-rose-700 bg-rose-50 border-rose-300'
}

export default function Discover({ onAccessRequest }) {
  const [modalTarget, setModalTarget] = useState(null)
  const [pendingRequests, setPendingRequests] = useState({})
  const [vaultToken, setVaultToken] = useState(null)
  const [loading, setLoading] = useState(true)
  const [companies, setCompanies] = useState(publicCompanies)
  const { toast } = useToast()

  useEffect(() => {
    let mounted = true
    setLoading(true)
    ;(async () => {
      const { data, error } = await companiesApi.listPublic()
      if (!mounted) return
      if (!error && data && data.length > 0) {
        setCompanies(data.map((c) => ({
          ...c,
          pitch: `${c.industry || 'Technology'} company based in ${c.country}`,
          traction: `${c.governance_score || 0}% governance score`,
          readiness: c.readiness_score || c.governance_score || 0,
        })))
      }
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [])

  const handleRequest = (company) => setModalTarget(company)

  const handleSubmit = async (payload) => {
    if (modalTarget) {
      setPendingRequests((prev) => ({ ...prev, [modalTarget.id]: true }))
      onAccessRequest?.({ ...payload, companyId: modalTarget.id, companyName: modalTarget.name })
      const { error } = await accessRequestsApi.create({
        company_id: modalTarget.id,
        investor_name: payload.name,
        investor_fund: payload.fund,
        investor_email: payload.email,
        investor_role: payload.role,
        stage_focus: payload.stageFocus || [],
        check_size: payload.checkSize,
      })
      if (error) {
        toast('Access request failed: ' + error, 'error')
      } else {
        toast(`Access request sent to ${modalTarget.name} founder.`, 'success')
      }
    }
    setModalTarget(null)
  }

  if (vaultToken) {
    return <InvestorVault token={vaultToken} onExit={() => setVaultToken(null)} />
  }

  return (
    <div className="min-h-full bg-neutral-50">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-9 h-9 bg-brand flex items-center justify-center">
              <Globe size={18} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-ink">Discover</h1>
          </div>
          <p className="text-sm text-muted">Verified companies seeking investment. Request vault access to review financials and compliance documents.</p>
        </div>

        <div className="border border-amber-200 bg-amber-50 px-5 py-3 flex items-start gap-2.5 mb-8">
          <ShieldAlert size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800">
            Businesses listed on this platform are not vetted or endorsed by Bizos Suite. All investment decisions are solely at your own risk.
          </p>
        </div>

        {loading ? (
          <SkeletonCardGrid count={6} />
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {publicCompanies.map((c) => {
            const pending = pendingRequests[c.id]
            return (
              <div key={c.id} className="surface p-6 flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-ink">{c.name}</h3>
                    <p className="text-xs text-neutral-500 mt-0.5">{c.country} · {c.industry}</p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${readinessColor(c.readiness)}`}>
                    <TrendingUp size={10} className="mr-1" /> {c.readiness}%
                  </span>
                </div>

                <p className="text-xs text-muted leading-relaxed mb-4 flex-1">{c.pitch}</p>

                <div className="flex items-center justify-between mb-4 text-xs">
                  <span className="text-neutral-500">Traction</span>
                  <span className="font-mono text-ink">{c.traction}</span>
                </div>

                <button
                  onClick={() => (pending ? setVaultToken('INV-TOK-0091') : handleRequest(c))}
                  disabled={pending}
                  className={pending ? 'btn-secondary text-xs' : 'btn-primary text-xs flex items-center gap-1.5 justify-center'}
                >
                  {pending ? 'Access Request Pending Founder Approval' : <>Request Vault Access <ArrowRight size={12} /></>}
                </button>
              </div>
            )
          })}
        </div>
        )}

        <AccessRequestModal
          open={!!modalTarget}
          onClose={() => setModalTarget(null)}
          company={modalTarget}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  )
}
